import { Eye, EyeOff, LockKeyhole } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth';
import { passwordRecoveryPath, safePortalReturn } from '../auth-routing';
import logoUrl from '../assets/nlm-logo.webp';
import './Login.css';

export default function Login({ client = false }: { client?: boolean }) {
  const { login } = useAuth();
  const location = useLocation();
  const next = client ? safePortalReturn(new URLSearchParams(location.search).get('next')) : null;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setError('');
    setBusy(true);
    try { await login(email.trim(), password); }
    catch (reason) { setError(reason instanceof Error ? reason.message : 'Sign in failed'); }
    finally { setBusy(false); }
  };

  return (
    <main className="login-page">
      <section className="login-panel" aria-labelledby="login-title">
        <div className="login-brand"><img src={logoUrl} alt="" /><span className="login-wordmark"><strong>NOLA</strong><small>No Limits Marketing</small></span></div>
        <div className="login-copy">
          <h1 id="login-title">{client ? 'Client portal' : 'Welcome back'}</h1>
          <p>{client ? 'Sign in to review your results, read reports, and follow requests with your No Limits Marketing team.' : "Sign in to review client health, active work, and performance across the agency."}</p>
        </div>
        {error && <div className="form-error" role="alert">{error}</div>}
        <form onSubmit={submit} className="login-form" aria-busy={busy}>
          <label htmlFor="email">{client ? "Email" : "Work email"}</label>
          <input id="email" name="email" type="email" autoComplete="username" autoCapitalize="none" spellCheck={false} maxLength={254} value={email} onChange={event => setEmail(event.target.value)} required />
          <label htmlFor="password">Password</label>
          <div className="password-field">
            <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} required />
            <button type="button" aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} onClick={() => setShowPassword(value => !value)}>
              {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
            </button>
          </div>
          <button className="button primary login-button" type="submit" disabled={busy}>
              <LockKeyhole aria-hidden="true" />{busy ? 'Signing in…' : client ? 'Sign in to your portal' : 'Sign in'}
          </button>
        </form>
        <Link className="recovery-back" to={passwordRecoveryPath(client, next)}>Forgot password?</Link>
        {client && <details className="login-help"><summary>First time here?</summary><p>Open your invitation from No Limits Marketing and choose your password. Then sign in with the email address that received the invitation.</p><p>If your team gave you a temporary password, sign in above. Need an invitation? Contact your agency team.</p></details>}
      </section>
      <LoginAside client={client} />
    </main>
  );
}

/** The panel beside the sign-in form. Password recovery shows the same one, so sign-in and reset read as one flow. */
export function LoginAside({ client = false }: { client?: boolean }) {
  return (
    <aside className="login-aside">
      <img className="login-watermark" src={logoUrl} alt="" aria-hidden="true" />
      <div>
        <h2>{client ? "Your marketing, in one place." : "Know what needs attention before the client asks."}</h2>
        <p>{client ? "Review performance, follow requests, and manage your account and workspace updates." : "Performance, delivery, communication, and source health come together in one focused workspace."}</p>
      </div>
    </aside>
  );
}
